import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MembershipRenewActions } from './membership-renew.actions';
import { exhaustMap, Observable, of, withLatestFrom } from 'rxjs';
import { Operation, OperationExecutePayload } from '@aaa/interface-joinRenew-joinRenewLib';
import { FormGroupValue } from '../../modules/share/form.utils';
import { AccountDetails, ValidateSucceededResponseObject } from '../types/types';
import { getMembershipRenewAccountDetails } from './membership-renew.selectors';
import { PaymentForm } from '@aaa/emember/share/payment-form';
import { ExecuteService } from '../services/execute.service';
import { ClubApp } from '@aaa/emember/types';
import { RequestError, RequestErrorType } from '../generic-errors';
import { filterByClubIds } from '../utils/filter-by-club-ids';
import { MembershipRenewForm } from './membership-renew.models';
import { DataLayerService } from '../../modules/share/services/data-layer.service';
import { getTransactionId } from '../utils/get-transaction-id';
import { AppAnalyticsEvents } from '@aaa/emember/types';
import {
  MembershipCampanaGetCostsRenewEvent,
  MembershipCampanaGetCostsResponse,
  MembershipCampanaMethod,
  MembershipCampanaOperationExecuteEvent,
  MembershipCampanaOperationExecuteResponse,
} from '@aaa/interface-joinRenew-membership-membershipCampana';
import { CampanaType } from '../campana.type';
import { checkOperationErrorsCampanaSystem } from '../check-operation-errors-campana-system';
import { checkMembershipErrorsCampanaSystem } from '../check-membership-errors-campana-system';
import { buildFreedompayPaymentEvent } from '../campana-utils';
import { checkCampanaPaymentValidation } from '../check-campana-payment-validation';

@Injectable({ providedIn: 'root' })
export class MembershipRenewCampanaSystemEffects {
  private store = inject(Store);
  private actions$ = inject(Actions).pipe(filterByClubIds([ClubApp.Northway]));
  private executeService = inject(ExecuteService);
  private dataLayer = inject(DataLayerService);

  setSummaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipRenewActions.recostValidateSucceeded),
      map(({ response }: ValidateSucceededResponseObject<MembershipCampanaGetCostsResponse>) => {
        const accountDetails = new CampanaType.AccountInfo(
          response.identity?.memberNumber,
          undefined,
          response.sessionResponse.data,
        );

        return MembershipRenewActions.setAccountDetails({ accountDetails });
      }),
    ),
  );

  recostValidateMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipRenewActions.recostValidate),
      withLatestFrom(this.store.select(getMembershipRenewAccountDetails)),
      switchMap(([, accountDetails]) =>
        this.recostValidate(accountDetails).pipe(
          map((res) => MembershipRenewActions.recostValidateSucceeded(res)),
          catchError((error) => of(MembershipRenewActions.recostValidateFailed({ error }))),
        ),
      ),
    ),
  );

  pay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipRenewActions.pay),
      // exhaustMap, not switchMap: a second Pay while the first is in flight is a
      // double charge, not a newer intent. switchMap drops our subscription but the execute is
      // already on the wire, and a session AXIS has since voided is rebuilt and completed again
      exhaustMap(({ params }) =>
        this.pay(params.accountDetails, params.formValues, params.executionData, params.totalCost, params.payment).pipe(
          map((accountDetails) => MembershipRenewActions.paySucceeded({ accountDetails })),
          catchError((error) => of(MembershipRenewActions.payFailed({ error }))),
        ),
      ),
    ),
  );

  pay(
    accountDetails: AccountDetails,
    formValues: FormGroupValue<MembershipRenewForm>,
    executionData: string,
    totalCost: number,
    payment: { token: string; formValues: FormGroupValue<PaymentForm> },
  ) {
    const membershipEvent: MembershipCampanaOperationExecuteEvent = {
        method: MembershipCampanaMethod.OPERATION_EXECUTE,
        operation: Operation.RENEW,

        executionData,
        // payment: {
        //   token: payment.token,
        //   expiry: '', // Todo: I cannot get info about expiry from iframe
        //   cardBrand: payment.formValues.card?.cardType || '', // Todo: I cannot get info about expiry from iframe
        //   holder: [formValues.billing?.billingTo?.firstName, formValues.billing?.billingTo?.firstName]
        //     .filter(Boolean)
        //     .join(' '),
        //   displayedAmount: totalCost,
        // } as MembershipCampanaHpfPaymentEvent,
      },
      paymentEvent = buildFreedompayPaymentEvent(Operation.RENEW, payment.token, totalCost),
      payload: OperationExecutePayload = {
        operation: Operation.RENEW,
        membershipEvent,
        paymentEvent,
      };

    return this.executeService
      .execute<
        CampanaType.ResponseObject<MembershipCampanaOperationExecuteResponse>,
        CampanaType.PaymentResponseObject
      >(payload)
      .pipe(
        map(({ validateObject, operationObject, paymentObject }) => {
          if (paymentObject?.meta?.isError || paymentObject?.error) {
            checkCampanaPaymentValidation(paymentObject.error || paymentObject);
          }

          if (validateObject?.meta.isError) {
            checkOperationErrorsCampanaSystem(
              validateObject.error || validateObject,
              validateObject,
              RequestErrorType.RenewMembership,
            );
          }

          const operationError = !!operationObject?.meta?.isError;
          if (operationError) {
            checkOperationErrorsCampanaSystem(operationObject.error, operationObject);
          }

          this.dataLayer.purchaseEvent(AppAnalyticsEvents.AccountRenewal, {
            itemId: 'primary',
            itemQuantity: 1,
            pageName: AppAnalyticsEvents.AccountRenewal,
            transactionId: getTransactionId(validateObject),
            itemPrice: totalCost,
            context: 'ava-store ' + AppAnalyticsEvents.AccountRenewal,
          });

          const membershipNumber = accountDetails?.memberNumber;

          return new CampanaType.AccountInfo(membershipNumber!, validateObject?.response);
        }),
      );
  }

  recostValidate(
    accountDetails: AccountDetails | null,
  ): Observable<ValidateSucceededResponseObject<MembershipCampanaGetCostsResponse>> {
    const payload: MembershipCampanaGetCostsRenewEvent = {
      method: MembershipCampanaMethod.GET_COSTS_RENEW,
      memberNumber: accountDetails?.memberNumber || '',
      verificationData: {
        lastName: accountDetails?.lastName || '',
        postalCode: accountDetails?.address.zip || '',
      },
    };

    return this.executeService
      .membershipQuery<CampanaType.ResponseObject<MembershipCampanaGetCostsResponse>>(payload)
      .pipe(
        map((validateObject) => {
          const { error, response, meta } = validateObject;
          const executionData = response?.executionData || '';

          if (meta.isError || error) {
            // the code picks the message: 4116 tells an auto renew member to call instead of paying
            checkMembershipErrorsCampanaSystem(error, validateObject, RequestErrorType.Error);
          }

          if (!executionData) {
            throw new RequestError(RequestErrorType.Error, validateObject);
          }

          return {
            executionData: executionData,
            response: response,
          };
        }),
      );
  }
}
